import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMsal } from '@azure/msal-react'
import { useQuery } from '@tanstack/react-query'
import {
	ArrowPathIcon,
	ChartBarIcon,
	CheckCircleIcon,
	CogIcon,
	DocumentTextIcon,
	UserGroupIcon,
	TableCellsIcon,
	LinkIcon,
	BoltIcon,
} from '@heroicons/react/24/outline'
import apiClient from '../../services/apiClient'
import { resolveRole } from '../../utils/roles'
import {
	DashboardActionItem,
	DashboardPhaseSummary,
	DashboardRole,
	DashboardStats,
	DashboardWizardSummary,
} from '../../types'
import PhaseSelectionModalV3 from './PhaseSelectionModalV3'

const isDebugLogging =
	(import.meta.env.VITE_LOGGING || '').toLowerCase() === 'debug'

const baseCardClass =
	'bg-white rounded-lg shadow-soft ' +
	'border border-gray-200'
const cardPaddingClass = 'p-6'
const statGridClass =
	'grid grid-cols-1 md:grid-cols-2 ' +
	'lg:grid-cols-4 gap-6'

const defaultStats: DashboardStats = {
	totalCustomers: 0,
	activePhases: 0,
	completedPhases: 0,
	totalRecords: 0,
	recentActivity: [],
	availableFields: 0,
	mappedFields: 0,
	applicationStatus: 'N/A',
}

function logDashboardAction(
	message: string,
	details?: Record<string, any>
): void {
	if (!isDebugLogging) return
	if (details) {
		console.log(`[DASHBOARD] ${message}`, details)
		return
	}
	console.log(`[DASHBOARD] ${message}`)
}

function formatProgress(
	wizard?: DashboardWizardSummary
): { percent: number; label: string } {
	if (!wizard) {
		return { percent: 0, label: '0%' }
	}
	if (typeof wizard.progressPercent === 'number') {
		const bounded = Math.max(0, Math.min(100, wizard.progressPercent))
		return { percent: bounded, label: `${bounded}%` }
	}
	if (wizard.totalSteps === 0) {
		return { percent: 0, label: '0%' }
	}
	const percent = Math.round(
		(wizard.completedSteps / wizard.totalSteps) * 100
	)
	return { percent, label: `${percent}%` }
}

function StatCard({
	icon: Icon,
	label,
	value,
	accentClass,
}: {
	icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
	label: string
	value: string | number
	accentClass: string
}) {
	const className = `${baseCardClass} ${cardPaddingClass}`
	return (
		<div className={className}>
			<div className="flex items-center">
				<Icon className={`w-8 h-8 ${accentClass}`} />
				<div className="ml-4">
					<p className="text-sm text-gray-500">{label}</p>
					<p className="text-2xl font-semibold text-gray-900">
						{value}
					</p>
				</div>
			</div>
		</div>
	)
}

function PhaseList({
	phases,
	role,
	title,
	emptyLabel,
}: {
	phases: DashboardPhaseSummary[]
	role: DashboardRole
	title: string
	emptyLabel: string
}) {
	return (
		<div className={`${baseCardClass} ${cardPaddingClass}`}>
			<h3 className="text-lg font-semibold text-gray-900 mb-4">
				{title}
			</h3>
			{phases.length === 0 ? (
				<p className="text-sm text-gray-500">{emptyLabel}</p>
			) : (
				<div className="space-y-4">
					{phases.map((phase) => {
						const progress = formatProgress(phase.wizard)
						const nextAccess = phase.wizard?.nextStepAccess
						const nextLabel = phase.wizard?.nextStepLabel
						const wizardRoute =
							`/customers/${phase.customerId}` +
							`/phases/${phase.phaseId}/wizard`
						const progressTrackClass =
							'mt-2 h-2 bg-gray-100 rounded-full'
						const progressFillClass =
							'h-2 bg-primary-600 rounded-full'
						const awaitingConsultant =
							role === 'customer' &&
							nextAccess === 'consultant'
						return (
							<div
								key={`${phase.customerId}-${phase.phaseId}`}
								className="border border-gray-200 rounded-lg p-4"
							>
								<div className="flex items-start justify-between">
									<div>
										<p className="text-sm text-gray-500">
											{phase.customerName}
										</p>
										<p className="text-base font-semibold text-gray-900">
											{phase.phaseName}
										</p>
									</div>
									<Link
										to={wizardRoute}
										className="text-sm text-primary-600 hover:text-primary-700 font-medium"
									>
										Open wizard
									</Link>
								</div>
								<div className="mt-3">
									<div className="flex items-center justify-between text-xs text-gray-500">
										<span>Progress</span>
										<span>{progress.label}</span>
									</div>
									<div className={progressTrackClass}>
										<div
											className={progressFillClass}
											style={{ width: `${progress.percent}%` }}
										></div>
									</div>
								</div>
								<div className="mt-3 text-sm text-gray-600">
									{awaitingConsultant
										? 'Awaiting consultant action'
										: nextLabel
											? `Next step: ${nextLabel}`
											: 'No next step available'}
								</div>
							</div>
						)
					})}
				</div>
			)}
		</div>
	)
}

function ActionList({
	items,
	title,
	emptyLabel,
}: {
	items: DashboardActionItem[]
	title: string
	emptyLabel: string
}) {
	return (
		<div className={`${baseCardClass} ${cardPaddingClass}`}>
			<h3 className="text-lg font-semibold text-gray-900 mb-4">
				{title}
			</h3>
			{items.length === 0 ? (
				<p className="text-sm text-gray-500">{emptyLabel}</p>
			) : (
				<div className="space-y-3">
					{items.map((item) => {
						const actionRoute =
							`/customers/${item.customerId}` +
							`/phases/${item.phaseId}/wizard`
						return (
							<div
								key={item.id}
								className="border border-gray-200 rounded-lg p-4"
							>
								<p className="text-sm font-semibold text-gray-900">
									{item.label}
								</p>
								<p className="text-sm text-gray-600 mt-1">
									{item.description}
								</p>
								<Link
									to={actionRoute}
									className="text-sm text-primary-600 hover:text-primary-700 mt-2 inline-block font-medium"
								>
									Open wizard
								</Link>
							</div>
						)
					})}
				</div>
			)}
		</div>
	)
}

function ActivityList({
	items,
	title,
}: {
	items: DashboardStats['recentActivity']
	title: string
}) {
	return (
		<div className={`${baseCardClass} ${cardPaddingClass}`}>
			<h3 className="text-lg font-semibold text-gray-900 mb-4">
				{title}
			</h3>
			{items.length === 0 ? (
				<p className="text-sm text-gray-500">No recent activity.</p>
			) : (
				<div className="space-y-3">
					{items.map((item) => (
						<div key={`${item.customerId}-${item.timestamp}`}>
							<p className="text-sm text-gray-900">
								{item.description}
							</p>
							<p className="text-xs text-gray-500">
								{new Date(item.timestamp).toLocaleString()}
							</p>
						</div>
					))}
				</div>
			)}
		</div>
	)
}

const Dashboard: React.FC = () => {
	const { instance } = useMsal()
	const fallbackRole = resolveRole(instance)

	const { data: dashboardData } = useQuery({
		queryKey: ['dashboard-stats'],
		queryFn: async () => {
			try {
				const response = await apiClient.get('/dashboard/stats')
				return response.data as DashboardStats
			} catch (error) {
				logDashboardAction('stats_error', { error })
				return defaultStats
			}
		},
	})

	const stats = dashboardData || defaultStats
	const role = stats.role || fallbackRole
	const phases = stats.phases || []
	const actionItems = stats.actionItems || []
	const atRiskPhases = stats.atRiskPhases || []
	const upcomingDeadlines = stats.upcomingDeadlines || []

	const [isPhaseModalOpen, setPhaseModalOpen] = useState(false)

	useEffect(() => {
		logDashboardAction('stats_loaded', {
			role,
			totalCustomers: stats.totalCustomers,
			activePhases: stats.activePhases,
		})
	}, [role, stats.totalCustomers, stats.activePhases])

	return (
		<div className="dashboard">
			<div className="mb-8">
				<h1 className="text-3xl font-bold text-gray-900 mb-2">
					SuccessFactors Migration Dashboard
				</h1>
				<p className="text-gray-600">
					{role === 'admin'
						? 'Global oversight and system health.'
						: role === 'consultant'
							? 'Your active customer migrations and tasks.'
							: 'Track your migration progress and results.'}
				</p>
			</div>

			{role === 'admin' && (
				<>
					<div className={`${statGridClass} mb-8`}>
						<StatCard
							icon={UserGroupIcon}
							label="Total customers"
							value={stats.totalCustomers}
							accentClass="text-primary-600"
						/>
						<StatCard
							icon={CogIcon}
							label="Active phases"
							value={stats.activePhases}
							accentClass="text-success-600"
						/>
						<StatCard
							icon={ChartBarIcon}
							label="Completed phases"
							value={stats.completedPhases}
							accentClass="text-purple-600"
						/>
						<StatCard
							icon={DocumentTextIcon}
							label="Records processed"
							value={stats.totalRecords.toLocaleString()}
							accentClass="text-accent-600"
						/>
					</div>

					<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
						<PhaseList
							phases={atRiskPhases}
							role={role}
							title="At-risk phases"
							emptyLabel="No phases are currently at risk."
						/>
						<ActionList
							items={actionItems}
							title="Action queue"
							emptyLabel="No immediate actions required."
						/>
					</div>

					<div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
						<PhaseList
							phases={phases.slice(0, 5)}
							role={role}
							title="Recent phases"
							emptyLabel="No phases available."
						/>
						<ActivityList
							items={stats.recentActivity}
							title="Recent activity"
						/>
					</div>
				</>
			)}

			{role === 'consultant' && (
				<>
					<div className={`${statGridClass} mb-8`}>
						<StatCard
							icon={UserGroupIcon}
							label="Assigned customers"
							value={stats.assignedCustomers || 0}
							accentClass="text-primary-600"
						/>
						<StatCard
							icon={CogIcon}
							label="Active phases"
							value={stats.activePhases}
							accentClass="text-success-600"
						/>
						<StatCard
							icon={CheckCircleIcon}
							label="Completed phases"
							value={stats.completedPhases}
							accentClass="text-purple-600"
						/>
						<StatCard
							icon={DocumentTextIcon}
							label="Records processed"
							value={stats.totalRecords.toLocaleString()}
							accentClass="text-accent-600"
						/>
					</div>

					<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
						<ActionList
							items={actionItems}
							title="Consultant action queue"
							emptyLabel="No consultant steps awaiting action."
						/>
						<PhaseList
							phases={upcomingDeadlines}
							role={role}
							title="Upcoming deadlines"
							emptyLabel="No deadlines in the next 30 days."
						/>
					</div>

					<div className="mt-6">
						<PhaseList
							phases={phases}
							role={role}
							title="Active phase progress"
							emptyLabel="No active phases assigned."
						/>
					</div>
				</>
			)}

			{role === 'customer' && (
				<>
					<div className={`${statGridClass} mb-8`}>
						<StatCard
							icon={CogIcon}
							label="Active phases"
							value={stats.activePhases}
							accentClass="text-success-600"
						/>
						<StatCard
							icon={TableCellsIcon}
							label="Available fields (to map)"
							value={stats.availableFields ?? 0}
							accentClass="text-primary-600"
						/>
						<StatCard
							icon={LinkIcon}
							label="Mapped fields"
							value={stats.mappedFields ?? 0}
							accentClass="text-purple-600"
						/>
						<StatCard
							icon={BoltIcon}
							label="Finalized Entities"
							value={stats.applicationStatus ?? 'N/A'}
							accentClass="text-accent-600"
						/>
					</div>

					<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
						<PhaseList
							phases={phases}
							role={role}
							title="Phase progress"
							emptyLabel="No phases available."
						/>
						<ActivityList
							items={stats.recentActivity}
							title="Recent activity"
						/>
					</div>
				</>
			)}

			<div className="mt-8">
				{/* Main Action Card: Wizard */}
				<div className={`${baseCardClass} ${cardPaddingClass} bg-gradient-to-br from-primary-50 via-white to-white border-primary-100 shadow-soft-lg group relative overflow-hidden transition-all duration-300 hover:shadow-soft`}>
					<div className="absolute top-0 right-0 p-8 opacity-[0.03] group-hover:opacity-[0.07] transition-opacity">
						<ArrowPathIcon className="w-64 h-64 text-primary-600 -mr-16 -mt-16 rotate-12" />
					</div>

					<div className="relative z-10 flex flex-col items-center text-center py-8">
						<div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mb-6 shadow-sm group-hover:scale-110 transition-transform duration-500">
							<ArrowPathIcon className="w-10 h-10 text-primary-600 font-bold" />
						</div>

						<h3 className="text-2xl font-bold text-gray-900 mb-3">
							Migration Wizard
						</h3>

						<p className="text-base text-gray-600 mb-8 max-w-2xl mx-auto">
							Launch your guided migration journey. Configure credentials, map fields, validate data, and export your results in one seamless workflow.
						</p>

						<div className="flex flex-col items-center gap-4">
							<button
								onClick={() => setPhaseModalOpen(true)}
								className="btn-primary inline-flex items-center px-8 py-4 text-lg font-semibold rounded-xl shadow-md hover:shadow-lg transition-all"
							>
								Start Migration Wizard
								<ArrowPathIcon className="ml-3 w-6 h-6 group-hover:rotate-180 transition-transform duration-700" />
							</button>
						</div>
					</div>
				</div>

				{/* Hidden for now: Data validation & Phase settings */}
				{/* 
				<div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
					<div className={`${baseCardClass} ${cardPaddingClass}`}>
						<DocumentTextIcon className="w-10 h-10 text-purple-600 mb-4" />
						<h3 className="text-lg font-semibold text-gray-900 mb-2">
							Data validation
						</h3>
						<p className="text-sm text-gray-600 mb-4">
							Review data quality issues and resolve blockers.
						</p>
						<Link to="/reports" className="btn-secondary inline-block">
							View reports
						</Link>
					</div>
					<div className={`${baseCardClass} ${cardPaddingClass}`}>
						<CogIcon className="w-10 h-10 text-success-600 mb-4" />
						<h3 className="text-lg font-semibold text-gray-900 mb-2">
							Phase settings
						</h3>
						<p className="text-sm text-gray-600 mb-4">
							Configure credentials, metadata, and export settings.
						</p>
						<Link to="/customers" className="btn-secondary inline-block">
							Manage phases
						</Link>
					</div>
				</div>
				*/}
			</div>

			<PhaseSelectionModalV3
				isOpen={isPhaseModalOpen}
				onClose={() => setPhaseModalOpen(false)}
				phases={phases}
			/>
		</div >
	)
}

export default Dashboard